'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { usePackCheckout } from '@/features/payment/hooks/use-pack-checkout';
import { CheckoutRailChooserDialog } from '@/features/payment/ui/CheckoutRailChooserDialog';

/**
 * Facade Kit buy CTA — a real in-page purchase: a teal capsule that mints the
 * correlation intent and opens the Paddle overlay, bound to the building. When
 * Paddle isn't available ({@link usePackCheckout} → `available` false: flag off /
 * no client token / no building / no server `checkout` descriptor, e.g. a
 * region Paddle refuses), it renders nothing — the caller shows the
 * server `web_payment_notice` banner instead, and the reference price stays
 * visible on the card. Web NEVER routes a buyer to the mobile app stores.
 *
 * The webhook owns the grant; this only launches checkout (API-driven state).
 */
export function PackBuyCta({
  projectId,
  verbLabel,
  calm = false,
  className,
  onLaunch,
  onPurchased,
  onClosed,
}: {
  /** Building the Kit binds to. Required for the Paddle path. */
  projectId: string | undefined;
  /** Verb CTA lead (e.g. "Get everything for "<name>""). Falls back to a
   *  generic localized buy label when unset. */
  verbLabel?: string;
  /** Calmer, outlined capsule for the repeat "Additional Facade Kit" buy. */
  calm?: boolean;
  className?: string;
  /**
   * Fired the instant the user taps buy, BEFORE the overlay opens. The paywall
   * uses it to relax its modal Radix `Dialog` to non-modal: Paddle's overlay
   * iframe is injected at `<body>` level, but a modal Dialog's
   * `react-remove-scroll` puts `pointer-events: none` on `<body>` + a focus
   * trap — which makes the body-level Paddle iframe render but stay unclickable.
   * Relaxing the lock (instead of closing the dialog) keeps the paywall mounted
   * so it can show the post-purchase success in place. On the storefront page
   * there is no host dialog, so this is unset.
   */
  onLaunch?: () => void;
  /** Fired on checkout.completed — the host flips to its success celebration. */
  onPurchased?: () => void;
  /** Fired on checkout.closed (purchase OR cancel) — the host re-locks. */
  onClosed?: () => void;
}) {
  const t = useTranslations('packs');
  const { available, alternative, isLaunching, startCheckout } = usePackCheckout(
    projectId,
    {
      onPurchased,
      onClosed,
    }
  );
  // Payment-rail chooser (Paddle vs T-Bank) — opened by the buy capsule when
  // the server shipped a `checkout_alternative` (RU-locale buyers on Paddle).
  const [chooserOpen, setChooserOpen] = useState(false);

  // No web checkout available (flag off / no client token / no building). The
  // caller carries the server `web_payment_notice`; web never shows an app-store
  // handoff, so there's nothing to render here.
  if (!available) {
    return null;
  }

  return (
    <div className={cn('flex w-full flex-col items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => {
          // Two rails available → let the buyer pick in a modal instead of
          // launching Paddle straight away.
          if (alternative) {
            setChooserOpen(true);
            return;
          }
          // Relax the host paywall's modal body-lock FIRST so Paddle's
          // body-level overlay is clickable, then launch (see onLaunch docs).
          onLaunch?.();
          void startCheckout();
        }}
        disabled={isLaunching}
        className={cn(
          // min-h + wrapping (not fixed height) so a long building name + long
          // currency can break to a second line instead of clipping.
          'inline-flex min-h-11 items-center justify-center whitespace-normal rounded-full px-6 py-2 text-center text-list-item-value font-semibold transition-opacity hover:opacity-90 disabled:opacity-60',
          calm
            ? 'border border-primary text-primary hover:bg-primary/5'
            : 'bg-primary text-primary-foreground'
        )}
      >
        {isLaunching
          ? t('checkoutOpening')
          : (verbLabel ?? t('getOneTimePack'))}
      </button>
      {alternative && (
        <CheckoutRailChooserDialog
          open={chooserOpen}
          onOpenChange={setChooserOpen}
          alternative={alternative}
          kind="pack"
          projectId={projectId}
          onPickPrimary={() => {
            setChooserOpen(false);
            onLaunch?.();
            void startCheckout();
          }}
        />
      )}
    </div>
  );
}
